import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Phone, Mail, MessageSquare, TrendingUp, Users, UserCheck, Clock, RefreshCw,
  ChevronLeft, ChevronRight, X, Bell, BellRing, BarChart3, PieChart,
  MessageCircle, PhoneCall, Send, CheckCircle, XCircle,
  ArrowUpRight, ArrowDownRight, Flame, Target
} from 'lucide-react';
import { AdminLayout } from '../components/layout/AdminLayout';
import { toast } from 'sonner';

const API_BASE = 'https://campusapi.ohcampus.com/apps/Chatbot';

const ChatbotLeads = () => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({ lead_type: '', status: '' });
  const [conversation, setConversation] = useState(null);
  const [showConversation, setShowConversation] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [notificationEnabled, setNotificationEnabled] = useState(true);
  const lastCheckRef = useRef(new Date().toISOString());
  const pollIntervalRef = useRef(null);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, ...filters });
      const response = await fetch(`${API_BASE}/getLeads?${params}`);
      const data = await response.json();
      if (data.response_code === '200') {
        setLeads(data.data.leads || []);
        setTotalPages(Math.ceil((data.data.total || 0) / 20));
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
    }
    setLoading(false);
  }, [page, filters]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_BASE}/getLeadStats`);
      const data = await response.json();
      if (data.response_code === '200') setStats(data.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await fetch(`${API_BASE}/getAnalytics`);
      const data = await response.json();
      if (data.response_code === '200') setAnalytics(data.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const checkNewLeads = useCallback(async () => {
    if (!notificationEnabled) return;
    try {
      const response = await fetch(`${API_BASE}/getNewLeadsCount?since=${encodeURIComponent(lastCheckRef.current)}`);
      const data = await response.json();
      if (data.response_code === '200' && data.data.count > 0) {
        data.data.leads.forEach(lead => {
          const emoji = lead.lead_type === 'hot' ? '🔥' : lead.lead_type === 'warm' ? '🌡️' : '❄️';
          toast.success(`New ${lead.lead_type?.toUpperCase()} Lead! ${emoji}`, {
            description: `${lead.name || 'Unknown'} - ${lead.interested_course || 'General inquiry'}`,
            duration: 8000,
            action: { label: 'View', onClick: () => { fetchLeads(); fetchStats(); } }
          });
        });
        lastCheckRef.current = data.data.last_check;
        fetchLeads();
        fetchStats();
      }
    } catch (error) {
      console.error('Error checking new leads:', error);
    }
  }, [notificationEnabled, fetchLeads]);

  const fetchConversation = async (sessionId) => {
    try {
      const response = await fetch(`${API_BASE}/getConversation?session_id=${sessionId}`);
      const data = await response.json();
      if (data.response_code === '200') {
        setConversation(data.data);
        setShowConversation(true);
      }
    } catch (error) {
      console.error('Error fetching conversation:', error);
    }
  };

  const updateLeadStatus = async (leadId, updates) => {
    try {
      const response = await fetch(`${API_BASE}/updateLeadStatus`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lead_id: leadId, ...updates })
      });
      const data = await response.json();
      if (data.response_code === '200') {
        toast.success('Lead updated successfully');
        fetchLeads();
        fetchStats();
      }
    } catch (error) {
      toast.error('Failed to update lead');
    }
  };

  useEffect(() => {
    fetchLeads();
    fetchStats();
    fetchAnalytics();
  }, [fetchLeads]);

  useEffect(() => {
    if (notificationEnabled) {
      pollIntervalRef.current = setInterval(checkNewLeads, 30000);
    }
    return () => { if (pollIntervalRef.current) clearInterval(pollIntervalRef.current); };
  }, [notificationEnabled, checkNewLeads]);

  const getLeadTypeColor = (type) => {
    switch (type) {
      case 'hot': return 'bg-red-100 text-red-700 border-red-200';
      case 'warm': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'cold': return 'bg-blue-100 text-blue-700 border-blue-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-green-100 text-green-700';
      case 'contacted': return 'bg-blue-100 text-blue-700';
      case 'converted': return 'bg-purple-100 text-purple-700';
      case 'lost': return 'bg-gray-100 text-gray-700';
      default: return 'bg-green-100 text-green-700';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const handleQuickAction = (action, lead) => {
    switch (action) {
      case 'call':
        window.open(`tel:${lead.phone}`, '_self');
        updateLeadStatus(lead.id, { status: 'contacted' });
        break;
      case 'whatsapp':
        window.open(`https://wa.me/${lead.phone?.replace(/[^0-9]/g, '')}?text=Hi ${lead.name || ''}, this is OhCampus team regarding your inquiry about ${lead.interested_course || 'college admission'}.`, '_blank');
        updateLeadStatus(lead.id, { status: 'contacted' });
        break;
      case 'email':
        window.open(`mailto:${lead.email}?subject=OhCampus - Your College Admission Inquiry&body=Hi ${lead.name || ''},\n\nThank you for your interest in ${lead.interested_course || 'college admissions'}.\n\nBest regards,\nOhCampus Team`, '_blank');
        break;
      case 'converted':
        updateLeadStatus(lead.id, { status: 'converted' });
        break;
      case 'lost':
        updateLeadStatus(lead.id, { status: 'lost' });
        break;
      default: break;
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">AI Chatbot Leads</h1>
            <p className="text-gray-500">Manage leads captured by the AI chatbot</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setNotificationEnabled(!notificationEnabled)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm border transition-all ${notificationEnabled ? 'bg-green-50 border-green-200 text-green-700' : 'bg-gray-50 border-gray-200 text-gray-500'}`}
            >
              {notificationEnabled ? <BellRing className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
              {notificationEnabled ? 'Live' : 'Paused'}
            </button>
            <button
              onClick={() => { setShowAnalytics(!showAnalytics); if (!showAnalytics) fetchAnalytics(); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm border ${showAnalytics ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}
            >
              <BarChart3 className="w-4 h-4" /> Analytics
            </button>
            <button onClick={() => { fetchLeads(); fetchStats(); fetchAnalytics(); }} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
          </div>
        </div>

        {/* Analytics Panel */}
        {showAnalytics && analytics && (
          <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl p-6 border border-slate-200">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <PieChart className="w-5 h-5 text-blue-600" /> Conversation Analytics
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Conversion Rate</span>
                  <Target className="w-4 h-4 text-purple-500" />
                </div>
                <p className="text-2xl font-bold text-purple-600 mt-1">{analytics.conversion_rate}%</p>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Avg Messages</span>
                  <MessageCircle className="w-4 h-4 text-blue-500" />
                </div>
                <p className="text-2xl font-bold text-blue-600 mt-1">{analytics.avg_messages}</p>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">This Week</span>
                  {analytics.weekly_growth >= 0 ? <ArrowUpRight className="w-4 h-4 text-green-500" /> : <ArrowDownRight className="w-4 h-4 text-red-500" />}
                </div>
                <p className="text-2xl font-bold text-gray-800 mt-1">{analytics.this_week}</p>
                <p className={`text-xs ${analytics.weekly_growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {analytics.weekly_growth >= 0 ? '+' : ''}{analytics.weekly_growth}% vs last week
                </p>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Hot Pending</span>
                  <Flame className="w-4 h-4 text-red-500" />
                </div>
                <p className="text-2xl font-bold text-red-600 mt-1">{analytics.hot_pending}</p>
                <p className="text-xs text-gray-500">Need action</p>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Top Interests</h4>
                <div className="space-y-2">
                  {analytics.by_course?.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">{item.interested_course || 'General'}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-500 rounded-full" style={{ width: `${(item.count / (analytics.by_course[0]?.count || 1)) * 100}%` }} />
                        </div>
                        <span className="text-sm font-medium text-gray-800 w-8">{item.count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Lead Sources</h4>
                <div className="space-y-2">
                  {analytics.by_source?.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 capitalize">{item.source || 'Unknown'}</span>
                      <span className="text-sm font-medium text-gray-800">{item.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-4 shadow-sm border hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg"><Users className="w-5 h-5 text-blue-600" /></div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{stats.total || 0}</p>
                  <p className="text-sm text-gray-500">Total Leads</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border hover:shadow-md transition-shadow cursor-pointer" onClick={() => setFilters({...filters, lead_type: 'hot'})}>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg"><TrendingUp className="w-5 h-5 text-red-600" /></div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{stats.by_type?.hot || 0}</p>
                  <p className="text-sm text-gray-500">Hot Leads</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border hover:shadow-md transition-shadow cursor-pointer" onClick={() => setFilters({...filters, lead_type: 'warm'})}>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg"><UserCheck className="w-5 h-5 text-orange-600" /></div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{stats.by_type?.warm || 0}</p>
                  <p className="text-sm text-gray-500">Warm Leads</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg"><Clock className="w-5 h-5 text-green-600" /></div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{stats.today || 0}</p>
                  <p className="text-sm text-gray-500">Today</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 shadow-sm border">
          <div className="flex flex-wrap gap-4 items-center">
            <select className="border rounded-lg px-3 py-2 text-sm bg-white" value={filters.lead_type} onChange={(e) => setFilters({...filters, lead_type: e.target.value})}>
              <option value="">All Lead Types</option>
              <option value="hot">🔥 Hot</option>
              <option value="warm">🌡️ Warm</option>
              <option value="cold">❄️ Cold</option>
            </select>
            <select className="border rounded-lg px-3 py-2 text-sm bg-white" value={filters.status} onChange={(e) => setFilters({...filters, status: e.target.value})}>
              <option value="">All Status</option>
              <option value="new">🆕 New</option>
              <option value="contacted">📞 Contacted</option>
              <option value="converted">✅ Converted</option>
              <option value="lost">❌ Lost</option>
            </select>
            {(filters.lead_type || filters.status) && (
              <button onClick={() => setFilters({ lead_type: '', status: '' })} className="text-sm text-gray-500 hover:text-gray-700 underline">Clear filters</button>
            )}
          </div>
        </div>

        {/* Leads Table */}
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lead</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Interest</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Remark</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading ? (
                  <tr><td colSpan="8" className="px-4 py-8 text-center text-gray-500">Loading...</td></tr>
                ) : leads.length === 0 ? (
                  <tr><td colSpan="8" className="px-4 py-8 text-center text-gray-500">No leads found</td></tr>
                ) : leads.map((lead) => (
                  <tr key={lead.id} className={`hover:bg-gray-50 ${lead.lead_type === 'hot' && (!lead.status || lead.status === 'new') ? 'bg-red-50' : ''}`}>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-800">{lead.name || 'Unknown'}</p>
                        <p className="text-xs text-gray-500">{lead.place || '-'}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="space-y-1">
                        {lead.phone && <span className="flex items-center gap-1 text-sm text-gray-600"><Phone className="w-3 h-3" />{lead.phone}</span>}
                        {lead.email && <span className="flex items-center gap-1 text-sm text-gray-600 truncate max-w-[150px]"><Mail className="w-3 h-3" />{lead.email}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{lead.interested_course || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-normal" title={lead.remark || "-"}>{lead.remark || "-"}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getLeadTypeColor(lead.lead_type)}`}>
                        {lead.lead_type === 'hot' ? '🔥' : lead.lead_type === 'warm' ? '🌡️' : '❄️'} {lead.lead_type?.toUpperCase() || 'UNKNOWN'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <select className={`px-2 py-1 rounded text-xs font-medium cursor-pointer ${getStatusColor(lead.status)}`} value={lead.status || 'new'} onChange={(e) => updateLeadStatus(lead.id, { status: e.target.value })}>
                        <option value="new">🆕 New</option>
                        <option value="contacted">📞 Contacted</option>
                        <option value="converted">✅ Converted</option>
                        <option value="lost">❌ Lost</option>
                      </select>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">{formatDate(lead.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        {lead.phone && (
                          <>
                            <button onClick={() => handleQuickAction('call', lead)} className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg" title="Call"><PhoneCall className="w-4 h-4" /></button>
                            <button onClick={() => handleQuickAction('whatsapp', lead)} className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg" title="WhatsApp"><Send className="w-4 h-4" /></button>
                          </>
                        )}
                        {lead.email && <button onClick={() => handleQuickAction('email', lead)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg" title="Email"><Mail className="w-4 h-4" /></button>}
                        <button onClick={() => fetchConversation(lead.session_id)} className="p-1.5 text-purple-600 hover:bg-purple-50 rounded-lg" title="View Conversation"><MessageSquare className="w-4 h-4" /></button>
                        <button onClick={() => handleQuickAction('converted', lead)} className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg" title="Mark Converted"><CheckCircle className="w-4 h-4" /></button>
                        <button onClick={() => handleQuickAction('lost', lead)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg" title="Mark Lost"><XCircle className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <p className="text-sm text-gray-500">Page {page} of {totalPages}</p>
            <div className="flex gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50"><ChevronLeft className="w-4 h-4" /></button>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        </div>

        {/* Conversation Modal */}
        {showConversation && conversation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl w-full max-w-lg max-h-[80vh] overflow-hidden shadow-2xl">
              <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                <div>
                  <h3 className="font-semibold">Conversation History</h3>
                  {conversation.lead && <p className="text-sm opacity-80">{conversation.lead.name || 'Unknown'} • {conversation.lead.interested_course || 'General'}</p>}
                </div>
                <button onClick={() => setShowConversation(false)} className="p-1 hover:bg-white/20 rounded"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-4 overflow-y-auto max-h-[60vh] space-y-3 bg-gray-50">
                {conversation.messages?.map((msg, idx) => (
                  <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] p-3 rounded-2xl text-sm ${msg.role === 'user' ? 'bg-blue-600 text-white rounded-br-md' : 'bg-white text-gray-800 shadow-sm rounded-bl-md'}`}>{msg.message}</div>
                  </div>
                ))}
              </div>
              {conversation.lead && (
                <div className="p-4 border-t bg-white">
                  <p className="text-xs text-gray-500 mb-2">Quick Actions</p>
                  <div className="flex gap-2">
                    {conversation.lead.phone && <button onClick={() => handleQuickAction('call', conversation.lead)} className="flex-1 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 flex items-center justify-center gap-1"><PhoneCall className="w-4 h-4" /> Call</button>}
                    {conversation.lead.phone && <button onClick={() => handleQuickAction('whatsapp', conversation.lead)} className="flex-1 py-2 bg-green-500 text-white rounded-lg text-sm hover:bg-green-600 flex items-center justify-center gap-1"><Send className="w-4 h-4" /> WhatsApp</button>}
                    {conversation.lead.email && <button onClick={() => handleQuickAction('email', conversation.lead)} className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 flex items-center justify-center gap-1"><Mail className="w-4 h-4" /> Email</button>}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default ChatbotLeads;
